import React, { useState } from 'react';
import {
  TIME_REQUEST_TYPES,
  TIME_PRESETS,
  QUICK_PRESETS,
  formatTimeForDisplay,
  formatTimeRequestForDisplay
} from '../utils/timeConstants';

/**
 * TimeRequestSelector - Structured time request input component
 *
 * Props:
 * - value: { type, time?, startTime?, endTime?, originalText? }
 * - onChange: (newValue) => void
 * - disabled: boolean
 */
export default function TimeRequestSelector({ value, onChange, disabled = false }) {
  const [showCustom, setShowCustom] = useState(false);

  const currentType = value?.type || TIME_REQUEST_TYPES.NONE;

  const handleQuickPreset = (preset) => {
    onChange({
      type: preset.type,
      time: preset.time,
      startTime: preset.startTime,
      endTime: preset.endTime
    });
    setShowCustom(false);
  };

  const handleClear = () => {
    onChange({ type: TIME_REQUEST_TYPES.NONE });
    setShowCustom(false);
  };

  const handleTypeChange = (newType) => {
    if (newType === TIME_REQUEST_TYPES.NONE) {
      onChange({ type: TIME_REQUEST_TYPES.NONE });
    } else if (newType === TIME_REQUEST_TYPES.BETWEEN) {
      onChange({
        type: newType,
        startTime: value?.startTime || '08:00',
        endTime: value?.endTime || '09:00'
      });
    } else {
      onChange({
        type: newType,
        time: value?.time || '08:30'
      });
    }
  };

  const handleTimeChange = (field, newTime) => {
    onChange({
      ...value,
      [field]: newTime
    });
  };

  const styles = {
    container: {
      marginBottom: '8px'
    },
    display: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '8px'
    },
    chip: {
      display: 'inline-flex',
      alignItems: 'center',
      background: '#e8f5e9',
      color: '#2e7d32',
      padding: '6px 12px',
      borderRadius: '16px',
      fontSize: '0.9em',
      fontWeight: '500'
    },
    removeBtn: {
      marginLeft: '6px',
      background: 'none',
      border: 'none',
      color: '#2e7d32',
      cursor: 'pointer',
      fontSize: '1em',
      padding: '0 4px'
    },
    quickPresets: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
      marginBottom: '8px'
    },
    presetBtn: {
      padding: '6px 12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      background: 'white',
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: '0.85em',
      opacity: disabled ? 0.6 : 1
    },
    presetBtnActive: {
      background: '#e8f5e9',
      borderColor: '#2e7d32',
      color: '#2e7d32'
    },
    customToggle: {
      padding: '4px 8px',
      border: 'none',
      background: 'none',
      color: '#1976d2',
      cursor: 'pointer',
      fontSize: '0.85em',
      textDecoration: 'underline'
    },
    customForm: {
      background: '#f8f9fa',
      padding: '12px',
      borderRadius: '4px',
      marginTop: '8px'
    },
    formRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '8px'
    },
    label: {
      minWidth: '60px',
      fontSize: '0.9em',
      color: '#666'
    },
    select: {
      padding: '6px 8px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '0.9em',
      minWidth: '100px'
    },
    noPreference: {
      color: '#666',
      fontStyle: 'italic',
      fontSize: '0.9em'
    }
  };

  // Check if a quick preset matches current value
  const isPresetActive = (preset) => {
    if (value?.type !== preset.type) return false;
    if (preset.type === TIME_REQUEST_TYPES.BETWEEN) {
      return value?.startTime === preset.startTime && value?.endTime === preset.endTime;
    }
    if (preset.type === TIME_REQUEST_TYPES.AFTER) {
      return value?.time === preset.time;
    }
    return false;
  };

  return (
    <div style={styles.container}>
      {/* Current selection display */}
      {currentType !== TIME_REQUEST_TYPES.NONE && (
        <div style={styles.display}>
          <span style={styles.chip}>
            {formatTimeRequestForDisplay(value)}
            {!disabled && (
              <button
                type="button"
                style={styles.removeBtn}
                onClick={handleClear}
                title="Clear time preference"
              >
                &times;
              </button>
            )}
          </span>
        </div>
      )}

      {/* Quick presets */}
      {!disabled && (
        <>
          <div style={styles.quickPresets}>
            {QUICK_PRESETS.map((preset, index) => (
              <button
                key={index}
                type="button"
                style={{
                  ...styles.presetBtn,
                  ...(isPresetActive(preset) ? styles.presetBtnActive : {})
                }}
                onClick={() => handleQuickPreset(preset)}
                disabled={disabled}
              >
                {preset.label}
              </button>
            ))}
            <button
              type="button"
              style={styles.customToggle}
              onClick={() => setShowCustom(!showCustom)}
            >
              {showCustom ? 'Hide Custom' : 'Custom...'}
            </button>
          </div>

          {/* Custom time selector */}
          {showCustom && (
            <div style={styles.customForm}>
              <div style={styles.formRow}>
                <span style={styles.label}>Type:</span>
                <select
                  style={styles.select}
                  value={currentType}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  disabled={disabled}
                >
                  <option value={TIME_REQUEST_TYPES.NONE}>No preference</option>
                  <option value={TIME_REQUEST_TYPES.EXACT}>Exact time</option>
                  <option value={TIME_REQUEST_TYPES.BEFORE}>Before</option>
                  <option value={TIME_REQUEST_TYPES.AFTER}>After</option>
                  <option value={TIME_REQUEST_TYPES.BETWEEN}>Between</option>
                </select>
              </div>

              {currentType === TIME_REQUEST_TYPES.EXACT && (
                <div style={styles.formRow}>
                  <span style={styles.label}>Time:</span>
                  <select
                    style={styles.select}
                    value={value?.time || '08:30'}
                    onChange={(e) => handleTimeChange('time', e.target.value)}
                    disabled={disabled}
                  >
                    {TIME_PRESETS.map((preset) => (
                      <option key={preset.value} value={preset.value}>
                        {preset.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {currentType === TIME_REQUEST_TYPES.BEFORE && (
                <div style={styles.formRow}>
                  <span style={styles.label}>Before:</span>
                  <select
                    style={styles.select}
                    value={value?.time || '09:00'}
                    onChange={(e) => handleTimeChange('time', e.target.value)}
                    disabled={disabled}
                  >
                    {TIME_PRESETS.map((preset) => (
                      <option key={preset.value} value={preset.value}>
                        {preset.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {currentType === TIME_REQUEST_TYPES.AFTER && (
                <div style={styles.formRow}>
                  <span style={styles.label}>After:</span>
                  <select
                    style={styles.select}
                    value={value?.time || '10:00'}
                    onChange={(e) => handleTimeChange('time', e.target.value)}
                    disabled={disabled}
                  >
                    {TIME_PRESETS.map((preset) => (
                      <option key={preset.value} value={preset.value}>
                        {preset.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {currentType === TIME_REQUEST_TYPES.BETWEEN && (
                <>
                  <div style={styles.formRow}>
                    <span style={styles.label}>From:</span>
                    <select
                      style={styles.select}
                      value={value?.startTime || '08:00'}
                      onChange={(e) => handleTimeChange('startTime', e.target.value)}
                      disabled={disabled}
                    >
                      {TIME_PRESETS.map((preset) => (
                        <option key={preset.value} value={preset.value}>
                          {preset.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={styles.formRow}>
                    <span style={styles.label}>To:</span>
                    <select
                      style={styles.select}
                      value={value?.endTime || '09:00'}
                      onChange={(e) => handleTimeChange('endTime', e.target.value)}
                      disabled={disabled}
                    >
                      {TIME_PRESETS.map((preset) => (
                        <option key={preset.value} value={preset.value}>
                          {preset.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* Disabled state - just show the value or no preference */}
      {disabled && currentType === TIME_REQUEST_TYPES.NONE && (
        <span style={styles.noPreference}>No preference</span>
      )}
    </div>
  );
}
